import React, { useEffect, useId, useState } from "react";
import { CheckCheck, ChevronDown, Smile } from "lucide-react";
import { ChatActivity } from "../../../services/chatService/ChatService";
import { getTimeAgo } from "../../../utilities/dateUtils";
import Tooltip from "../../ui/Tooltip/Tooltip";

export const isUnreadReactionActivity = (activity: ChatActivity): boolean =>
    !activity.read && activity.activityType === "chat.reaction";

export interface ReactionBadge {
    count: number;
    emoji?: string;
}

/** Groups unread reaction activity by conversation for the conversation-row badges. */
export const getReactionBadges = (activities: ChatActivity[]): Record<number, ReactionBadge> =>
    activities.reduce<Record<number, ReactionBadge>>((badges, activity) => {
        if (!isUnreadReactionActivity(activity)) return badges;
        const current = badges[activity.conversationId];
        badges[activity.conversationId] = {
            count: (current?.count ?? 0) + 1,
            emoji: current?.emoji || activity.reactionType?.trim() || undefined,
        };
        return badges;
    }, {});

/** Unread reaction activity ids in one conversation; opening that conversation clears them all. */
export const getConversationReactionActivityIds = (activities: ChatActivity[], conversationId: number): number[] =>
    activities
        .filter(activity => isUnreadReactionActivity(activity) && activity.conversationId === conversationId)
        .map(activity => activity.id);

/**
 * Where the popup's "Open chat page" button goes. Unread mentions and thread replies are not
 * listed in the popup, so they route to the activity view; otherwise keep the open conversation.
 */
export const getPopupExpandTarget = (activities: ChatActivity[], selectedConversationId: number): string => {
    if (activities.some(activity => !activity.read && activity.activityType !== "chat.reaction")) return "/chat?view=activity";
    return selectedConversationId > 0 ? `/chat/${selectedConversationId}` : "/chat";
};

/**
 * Persists read state, then announces it to other chat views (e.g. Chat Activity). Announcing
 * only after success keeps those views from showing items as read when the save failed.
 */
export const persistActivitiesRead = async (activityIds: number[], persist: (ids: number[]) => Promise<unknown>): Promise<boolean> => {
    if (activityIds.length === 0) return true;
    try {
        await persist(activityIds);
    } catch (error) {
        console.error("Unable to mark chat reactions as read", error);
        return false;
    }
    window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: { readActivityIds: activityIds } }));
    return true;
};

export const formatReactionCount =(count: number): string => `${count} new ${count === 1 ? "reaction" : "reactions"}`;

const actorName = (activity: ChatActivity) => activity.actorDisplayName?.trim() || "Someone";

const distinct = (values: string[]) => values.filter((value, index) => value && values.indexOf(value) === index);

const summarizeActors = (activities: ChatActivity[]): string => {
    const names = distinct(activities.map(actorName));
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
};

interface PopupReactionActivityProps {
    /** Unread reaction activity, newest first. */
    activities: ChatActivity[];
    onOpenActivity: (activity: ChatActivity) => void;
    onMarkAllRead: () => void;
}

const PopupReactionActivity: React.FC<PopupReactionActivityProps> = ({ activities, onOpenActivity, onMarkAllRead }) => {
    const [expanded, setExpanded] = useState(false);
    const listId = useId();
    const count = activities.length;

    useEffect(() => {
        if (count <= 1) setExpanded(false);
    }, [count]);

    if (count === 0) return null;

    const emojis = distinct(activities.map(activity => activity.reactionType?.trim() || "")).slice(0, 3);
    const actors = summarizeActors(activities);
    const single = count === 1;

    return (
        <section aria-label="New reactions" className="popup-reaction-activity mx-1.5 mb-1 shrink-0 rounded-xl bg-[#7E57C2]/[0.06] dark:bg-purple-400/10">
            <div className="flex items-center gap-1 rounded-xl pr-1.5 has-[.popup-reaction-summary:focus-visible]:ring-2 has-[.popup-reaction-summary:focus-visible]:ring-[#7E57C2]/40 dark:has-[.popup-reaction-summary:focus-visible]:ring-purple-300/50">
                <button
                    type="button"
                    onClick={() => (single ? onOpenActivity(activities[0]) : setExpanded(current => !current))}
                    aria-expanded={single ? undefined : expanded}
                    aria-controls={single ? undefined : listId}
                    className="popup-reaction-summary flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border-0 bg-transparent px-2.5 py-2 text-left outline-none transition-colors hover:bg-[#7E57C2]/[0.06] focus:outline-none focus-visible:ring-0 motion-reduce:transition-none dark:hover:bg-purple-400/10"
                >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#7E57C2]/10 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300" aria-hidden="true">
                        <Smile size={16} strokeWidth={2.1} />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] font-semibold text-slate-900 dark:text-slate-100">{formatReactionCount(count)}</span>
                        <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400" title={actors}>
                            {emojis.length > 0 && <span aria-hidden="true">{emojis.join(" ")} · </span>}
                            {actors}
                        </span>
                    </span>
                    {!single && (
                        <ChevronDown
                            size={15}
                            aria-hidden="true"
                            className={`shrink-0 text-slate-400 transition-transform motion-reduce:transition-none dark:text-slate-500 ${expanded ? "rotate-180" : ""}`}
                        />
                    )}
                </button>
                <Tooltip content="Mark all read" position="bottom">
                    <button
                        type="button"
                        onClick={onMarkAllRead}
                        aria-label="Mark all reactions as read"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent p-0 text-[#6D4DB3] outline-none transition-colors hover:bg-[#7E57C2]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/40 motion-reduce:transition-none dark:text-purple-300 dark:hover:bg-purple-400/15 dark:focus-visible:ring-purple-300/50"
                    >
                        <CheckCheck size={16} />
                    </button>
                </Tooltip>
            </div>
            {!single && expanded && (
                <ul id={listId} className="m-0 max-h-44 list-none overflow-y-auto px-1.5 pb-1.5 pt-0 scrollbar-thin">
                    {activities.map(activity => {
                        const reaction = activity.reactionType?.trim();
                        return (
                            <li key={activity.id} className="list-none">
                                <button
                                    type="button"
                                    onClick={() => onOpenActivity(activity)}
                                    className="flex w-full items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1.5 text-left outline-none transition-colors hover:bg-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/40 motion-reduce:transition-none dark:hover:bg-slate-800"
                                >
                                    <span className="flex w-5 shrink-0 justify-center text-[13px] leading-none text-[#7E57C2] dark:text-purple-300" aria-hidden="true">
                                        {reaction || <Smile size={14} />}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-[12px] text-slate-700 dark:text-slate-200">
                                        {actorName(activity)} reacted to one of your messages
                                        {reaction && <span className="sr-only"> with {reaction}</span>}
                                    </span>
                                    <span className="shrink-0 text-[10px] text-slate-400 dark:text-slate-500">{getTimeAgo(activity.createdAt)}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};

export default PopupReactionActivity;
